import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography, Button, Space, Input, Form,
    Divider, Row, Col, Avatar, Alert, Card,
    Spin, Skeleton,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
    ArrowLeftOutlined, SafetyCertificateOutlined, InfoCircleOutlined, UserOutlined, SaveOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { profilsService } from '../../services/ProfileService';
import { habilitationsService } from '../../services/HabilitationsService';
import HabilitationSelector from '../../components/HabilitationSelector';

const { Title, Text } = Typography;



// ── Page ProfilEdit ───────────────────────────────────────────────────────────
export default function ProfilEdit() {
    const { t } = useTranslation('profils');
    const navigate = useNavigate();
    const { id } = useParams(); // profilTrackingId via la route /profils/:id/edit

    const [habilitations, setHabilitations] = useState([]);
    const [loadingHabs, setLoadingHabs] = useState(false);
    const [loadingProfil, setLoadingProfil] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const [selectedIds, setSelectedIds] = useState([]);

    const loadHabilitations = useCallback(async () => {
        setLoadingHabs(true);
        try {
            const res = await habilitationsService.list();
            setHabilitations(res.data?.data || res.data || []);
        } catch {
            toast.error(i18n.t('profils:commun.erreurChargementHabilitations'));
        } finally {
            setLoadingHabs(false);
        }
    }, []);

    const loadProfil = useCallback(async () => {
        setLoadingProfil(true);
        try {
            const res = await profilsService.get(id); // ou list() puis .find()
            const profil = res.data?.data || res.data;
            const ids = profil.habilitations?.map((h) => h.habilitationTrackingId) || [];
            setSelectedIds(ids);
            form.setFieldsValue({
                profilNom: profil.profilNom,
                commentaire: profil.commentaire,
                organisme: profil.organisme?.raisonSociale || i18n.t('profils:commun.global'),
            });
        } catch {
            toast.error(i18n.t('profils:edition.erreurChargement'));
            navigate('/profils');
        } finally {
            setLoadingProfil(false);
        }
    }, [id, form, navigate]);

    useEffect(() => {
        loadHabilitations();
        loadProfil();
    }, [loadHabilitations, loadProfil]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);
            await profilsService.update(id, {
                profilNom: values.profilNom,
                commentaire: values.commentaire || '',
                habilitationTrackingIds: selectedIds,
                organismeTrackingId: null,
            });
            toast.success(t('edition.succes'));
            navigate('/profils');
        } catch (err) {
            if (err?.errorFields) return;
            toast.error(err?.response?.data?.message || t('edition.erreur'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout title={t('edition.titreOnglet')}>
            <div style={{ padding: '28px 32px', background: '#f9fafb', minHeight: '100vh' }}>

                {/* ── Header ── */}
                <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/profils')}
                        type="text"
                        style={{ color: '#6b7280' }}
                        disabled={saving}
                    />
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#111827' }}>
                            {t('edition.titre')}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {t('edition.sousTitre')}
                        </Text>
                    </div>
                </div>

                <Alert
                    type="info" showIcon icon={<InfoCircleOutlined />}
                    message={t('commun.aideModule')}
                    style={{ marginBottom: 24, fontSize: 12 }}
                />

                {loadingProfil ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                ) : (
                    <Form form={form} layout="vertical" requiredMark="optional">
                        <Row gutter={24}>
                            {/* Colonne infos */}
                            <Col xs={24} lg={8}>
                                <Card
                                    title={
                                        <Space>
                                            <Avatar size={28} icon={<SafetyCertificateOutlined />}
                                                style={{ background: '#e8f5ed', color: '#1a6b3c' }} />
                                            <span>{t('commun.informations')}</span>
                                        </Space>
                                    }
                                    style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}
                                >
                                    <Form.Item
                                        name="profilNom"
                                        label={t('commun.nomProfil')}
                                        rules={[{ required: true, message: t('commun.nomObligatoire') }]}
                                        tooltip={t('commun.nomExemples')}
                                    >
                                        <Input placeholder={t('commun.nomPlaceholder')} size="large" />
                                    </Form.Item>

                                    <Form.Item name="organisme" label={t('commun.organisme')}
                                        tooltip={t('commun.organismeAide')}>
                                        <Input disabled placeholder={t('commun.organismePlaceholder')}
                                            prefix={<UserOutlined style={{ color: '#9ca3af' }} />} />
                                    </Form.Item>

                                    <Form.Item name="commentaire" label={t('commun.commentaire')}>
                                        <Input.TextArea
                                            rows={4}
                                            placeholder={t('commun.commentairePlaceholder')}
                                            maxLength={500} showCount
                                        />
                                    </Form.Item>
                                </Card>
                            </Col>

                            {/* Colonne habilitations */}
                            <Col xs={24} lg={16}>
                                <Spin spinning={loadingHabs} tip={t('commun.chargementHabilitations')}>
                                    <HabilitationSelector
                                        habilitations={habilitations}
                                        value={selectedIds}
                                        onChange={setSelectedIds}
                                    />
                                </Spin>
                            </Col>
                        </Row>

                        {/* ── Actions ── */}
                        <Divider />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                            <Button onClick={() => navigate('/profils')} disabled={saving}>
                                {t('commun.annuler')}
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={saving}
                                disabled={saving}
                                onClick={handleSave}
                                size="large"
                                style={{ background: saving ? undefined : '#1a6b3c', minWidth: 180 }}
                            >
                                {saving ? t('commun.enregistrement') : t('edition.enregistrer')}
                            </Button>
                        </div>
                    </Form>
                )}
            </div>
        </Layout>
    );
}